import { useState } from 'react';
import { Ticket, X } from 'lucide-react';
import {
  describeDiscountValue,
  describeVoucherValue,
  presetTouchesCart,
  type PosDiscountPreset,
} from '@mlm/shared';
import { Button, Input } from '@mlm/ui';
import type { PosVoucher } from '../../lib/api';
import type { SaleCoupon } from '../../lib/cart-store';
import { ManagerPinForm } from './manager-pin-form';

/**
 * The discounts a cashier can apply without typing a figure: the buyer's gift
 * checks, the branch's preset buttons, and a box for a code the customer hands
 * over (a discount code, or a gift check's code or QR). A sale carries one of
 * them at a time, so pressing one replaces the other; pressing the lit button
 * again takes it off.
 *
 * A button that touches nothing in the cart is greyed out rather than refused
 * after the tap. A preset marked "needs a manager PIN" applies straight away, so
 * the customer sees the price, and asks for the PIN before the sale can be
 * charged. A gift check never needs one: it is the customer's own.
 */
export function DiscountPresets({
  presets,
  codesEnabled,
  vouchers,
  vouchersEnabled,
  voucherLabel,
  coupon,
  couponChip,
  cartProductIds,
  error,
  onCoupon,
  onApplyCode,
  onChip,
}: {
  presets: PosDiscountPreset[];
  codesEnabled: boolean;
  /** The buyer's usable gift checks. */
  vouchers: PosVoucher[];
  /** Gift checks are on, so the code box also takes a gift check's code. */
  vouchersEnabled: boolean;
  /** What the tenant calls a gift check, singular. */
  voucherLabel: string;
  coupon: SaleCoupon | null;
  couponChip: string | null;
  cartProductIds: string[];
  /** Why the last discount was refused or taken off again, if it was. */
  error: string | null;
  onCoupon: (coupon: SaleCoupon | null) => void;
  /** Look a typed code up and apply it. Resolves false when it was refused. */
  onApplyCode: (code: string) => Promise<boolean>;
  onChip: (chip: string | null) => void;
}) {
  const [codeDraft, setCodeDraft] = useState('');
  const [checking, setChecking] = useState(false);
  const [pinMode, setPinMode] = useState(false);
  const codeBox = codesEnabled || vouchersEnabled;
  if (presets.length === 0 && vouchers.length === 0 && !codeBox) return null;

  const needsPin = coupon?.kind === 'preset' && coupon.requiresManagerPin && !couponChip;
  const gcWord = voucherLabel.toLowerCase();
  // A gift check applied by its code shows as its button when the list has it,
  // and as a chip otherwise (the list was still loading, or it just changed).
  const voucherChip =
    coupon?.kind === 'voucher' && !vouchers.some((v) => v.id === coupon.id) ? coupon : null;
  const placeholder =
    codesEnabled && vouchersEnabled
      ? `Discount or ${gcWord} code`
      : vouchersEnabled
        ? `${voucherLabel} code`
        : 'Discount code';

  async function applyCode() {
    const code = codeDraft.trim();
    if (!code || checking) return;
    setChecking(true);
    try {
      if (await onApplyCode(code)) {
        setCodeDraft('');
        setPinMode(false);
      }
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="text-lg font-semibold">Discounts</div>

      {vouchers.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {vouchers.map((v) => {
            const selected = coupon?.kind === 'voucher' && coupon.id === v.id;
            const usable = presetTouchesCart(v, cartProductIds);
            return (
              <button
                key={v.id}
                type="button"
                aria-pressed={selected}
                disabled={!usable && !selected}
                onClick={() => {
                  setPinMode(false);
                  onCoupon(
                    selected
                      ? null
                      : { kind: 'voucher', id: v.id, code: v.code, name: v.programName },
                  );
                }}
                className={
                  'min-h-[56px] rounded-lg border-2 border-dashed px-3 py-2 text-left disabled:opacity-40 ' +
                  (selected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-muted')
                }
              >
                <div className="flex items-center gap-1.5 text-base font-semibold leading-tight">
                  <Ticket className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="truncate">{v.programName}</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  <span className="font-mono">{v.code}</span> · {describeVoucherValue(v)}
                  {v.expiresAt
                    ? ` · until ${new Date(v.expiresAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}`
                    : ''}
                  {!usable && !selected ? ' · not for these items' : ''}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {presets.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {presets.map((p) => {
            const selected = coupon?.kind === 'preset' && coupon.id === p.id;
            const usable = presetTouchesCart(p, cartProductIds);
            return (
              <button
                key={p.id}
                type="button"
                aria-pressed={selected}
                disabled={!usable && !selected}
                onClick={() => {
                  setPinMode(false);
                  onCoupon(
                    selected
                      ? null
                      : {
                          kind: 'preset',
                          id: p.id,
                          name: p.name,
                          requiresManagerPin: p.requiresManagerPin,
                        },
                  );
                }}
                className={
                  'min-h-[56px] rounded-lg border-2 px-3 py-2 text-left disabled:opacity-40 ' +
                  (selected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-transparent bg-muted')
                }
              >
                <div className="text-base font-semibold leading-tight">{p.name}</div>
                <div className="text-sm text-muted-foreground">
                  {describeDiscountValue(p)}
                  {p.requiresManagerPin ? ' · manager PIN' : ''}
                  {!usable && !selected ? ' · not for these items' : ''}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {codeBox &&
        (coupon?.kind === 'code' || voucherChip ? (
          <div className="flex h-12 items-center justify-between rounded-lg border-2 border-primary bg-primary/10 px-3 text-primary">
            <span className="text-base font-semibold">
              {coupon?.kind === 'code'
                ? `Code ${coupon.code}`
                : `${voucherLabel} ${voucherChip!.code}`}
            </span>
            <button
              type="button"
              aria-label="Remove this discount"
              onClick={() => onCoupon(null)}
              className="flex h-12 w-12 items-center justify-center"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void applyCode();
            }}
          >
            <Input
              className="h-12 flex-1 text-base uppercase"
              value={codeDraft}
              onChange={(e) => setCodeDraft(e.target.value)}
              placeholder={placeholder}
              aria-label={placeholder}
              autoCapitalize="characters"
              autoComplete="off"
              maxLength={64}
            />
            <Button
              type="submit"
              variant="outline"
              className="h-12"
              disabled={!codeDraft.trim() || checking}
            >
              {checking ? 'Checking…' : 'Apply'}
            </Button>
          </form>
        ))}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {coupon?.kind === 'preset' &&
        coupon.requiresManagerPin &&
        (couponChip ? (
          <div className="text-sm text-success">✓ Manager approval recorded.</div>
        ) : !pinMode ? (
          <Button variant="outline" className="h-12" onClick={() => setPinMode(true)}>
            {coupon.name}: manager PIN required
          </Button>
        ) : (
          <ManagerPinForm
            onApproved={(chip) => {
              onChip(chip);
              setPinMode(false);
            }}
            onCancel={() => setPinMode(false)}
          />
        ))}
      {needsPin && !pinMode && (
        <p className="text-sm text-warning">A manager PIN is needed before charging.</p>
      )}
    </div>
  );
}
